import { lt, createZodFormResolver, showSuccessAlert, showErrorAlert, useFormControls } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { EmailWhitelistConfiguration, EmailWhitelistUserData } from '@mews-commander/data-access';
import { useAddWhitelistedUserEmails, useUpdateWhitelistedUserEmails } from '../actions';
import { createWhitelistedEmailConfigurationSchema, WhitelistedEmailConfigurationModel } from './schema';

interface WhitelistedUserEmailFormProps {
    enterpriseId: string;
    selectedRow: EmailWhitelistConfiguration | null;
    closeDialog: () => void;
    availableUsers: EmailWhitelistUserData[];
}

export const useWhitelistedUserEmailForm = ({
    selectedRow,
    enterpriseId,
    closeDialog,
    availableUsers,
}: WhitelistedUserEmailFormProps) => {
    const availableEmails = [...availableUsers.map((user) => user.email), ...(selectedRow ? [selectedRow.email] : [])];

    const resolver = createZodFormResolver<WhitelistedEmailConfigurationModel>(
        createWhitelistedEmailConfigurationSchema({ availableEmails })
    );

    const { mutateAsync: updateWhitelistedUser } = useUpdateWhitelistedUserEmails({
        onError: () => {
            showErrorAlert({ description: lt(TextKey.WhitelistedUserEmailsUpdateAlertError) });
        },
    });

    const { mutateAsync: addWhitelistedUser } = useAddWhitelistedUserEmails({
        onError: () => {
            showErrorAlert({ description: lt(TextKey.WhitelistedUserEmailsAddAlertError) });
        },
    });

    return useFormControls<WhitelistedEmailConfigurationModel>({
        onSubmit: async ({ email, emailTypes }) => {
            try {
                const userId = availableUsers.find((user) => user.email === email)?.id ?? null;

                if (selectedRow != null) {
                    await updateWhitelistedUser({ updates: [{ id: selectedRow.id, emailTypes }] });
                    showSuccessAlert({
                        description: lt(TextKey.WhitelistedUserEmailsUpdateAlertSuccess, { email: selectedRow.email }),
                    });
                } else if (userId != null) {
                    await addWhitelistedUser({
                        configurations: [
                            {
                                enterpriseId,
                                userId,
                                emailTypes,
                            },
                        ],
                    });
                    showSuccessAlert({ description: lt(TextKey.WhitelistedUserEmailsAddAlertSuccess, { email }) });
                }
                closeDialog();
                // eslint-disable-next-line @mews/no-empty-catch
            } catch (error) {
                // No action needed here, error handling is done in the mutation
            }
        },
        shouldFocusError: true,
        resolver,
        defaultValues: {
            email: selectedRow?.email ?? '',
            emailTypes: selectedRow?.emailTypes ?? [],
        },
    });
};
